/**
 * Scripts that must not live inside Vue’s #app innerHTML-as-template output.
 * Vue’s template compiler strips <script> / <style>; moving behavior here keeps it reliable.
 */

/**
 * FAQ <details> mutual-exclusivity fallback for browsers that ignore name="...".
 * Must not live as an inline <script> inside #app (Vue strips it).
 */
export function initFaqAccordion() {
    if (typeof document === 'undefined') return;
    if (window.__faqAccordionBound) return;
    if (!document.querySelector('[data-faq-group]')) return;

    window.__faqAccordionBound = true;

    document.addEventListener('toggle', function (event) {
        const target = event.target;
        if (!target || target.tagName !== 'DETAILS' || !target.open) return;
        const group = target.closest('[data-faq-group]');
        if (!group) return;
        const name = target.getAttribute('name');
        if (!name) return;
        group.querySelectorAll('details[name="' + name + '"][open]').forEach(function (other) {
            if (other !== target) other.open = false;
        });
    }, true);
}

/** Top-level <details> in desktop header nav. */
export function initHeaderNavDropdowns() {
    if (typeof window === 'undefined') return;
    if (window.__headerNavDropdownsBound) return;

    const header = document.querySelector('header.site-print-hidden');
    if (!header) return;
    const nav = header.querySelector('nav');
    if (!nav) return;

    window.__headerNavDropdownsBound = true;

    function topLevelDetails() {
        return Array.prototype.slice.call(nav.querySelectorAll(':scope > details'));
    }

    function closeAllDetails() {
        nav.querySelectorAll('details').forEach(function (d) {
            d.removeAttribute('open');
        });
    }

    function closeOthers(current) {
        nav.querySelectorAll('details').forEach(function (d) {
            if (d !== current) d.removeAttribute('open');
        });
    }

    topLevelDetails().forEach(function (d) {
        d.addEventListener('toggle', function () {
            if (!d.open) return;
            closeOthers(d);
        });

        const summary = d.querySelector(':scope > summary');
        if (summary) {
            summary.addEventListener('click', function () {
                if (!d.open) closeOthers(d);
            });
        }
    });

    function onPointerOutside(e) {
        if (nav.contains(e.target)) return;
        closeAllDetails();
    }

    document.addEventListener('pointerdown', onPointerOutside, true);
    document.addEventListener('click', onPointerOutside, true);

    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllDetails();
    });
}

/** Blog article sidebar: highlight the active table-of-contents item on click and while scrolling. */
export function initBlogArticleToc() {
    if (typeof document === 'undefined') return;
    if (window.__blogArticleTocBound) return;

    const navs = document.querySelectorAll('nav.blog-article-toc');
    if (!navs.length) return;

    window.__blogArticleTocBound = true;

    navs.forEach(function (nav) {
        const links = Array.from(nav.querySelectorAll('a.blog-article-toc-link'));
        if (!links.length) return;

        const sectionIds = links.map(function (link) {
            return link.dataset.tocTarget || (link.getAttribute('href') || '').replace(/^#/, '');
        }).filter(Boolean);

        const sections = sectionIds
            .map(function (id) {
                return document.getElementById(id);
            })
            .filter(Boolean);

        function setActive(id) {
            if (!id) return;

            links.forEach(function (link) {
                const linkId = link.dataset.tocTarget || (link.getAttribute('href') || '').replace(/^#/, '');
                const isActive = linkId === id;
                link.classList.toggle('active', isActive);
                if (!isActive) return;

                const scroller = link.closest('[data-toc-scroll]');
                if (!scroller) return;

                const linkRect = link.getBoundingClientRect();
                const boxRect = scroller.getBoundingClientRect();
                if (linkRect.top < boxRect.top) {
                    scroller.scrollTop -= boxRect.top - linkRect.top;
                } else if (linkRect.bottom > boxRect.bottom) {
                    scroller.scrollTop += linkRect.bottom - boxRect.bottom;
                }
            });
        }

        links.forEach(function (link) {
            link.addEventListener('click', function () {
                const id = link.dataset.tocTarget || (link.getAttribute('href') || '').replace(/^#/, '');
                setActive(id);
            });
        });

        function syncActiveFromScroll() {
            if (!sections.length) return;

            const offset = 96;
            let activeId = null;
            let activeTop = -Infinity;

            for (let i = 0; i < sections.length; i++) {
                const top = sections[i].getBoundingClientRect().top;
                if (top <= offset && top > activeTop) {
                    activeTop = top;
                    activeId = sections[i].id;
                }
            }

            setActive(activeId || sectionIds[0] || null);
        }

        if (sections.length) {
            window.addEventListener('scroll', syncActiveFromScroll, { passive: true });
            window.addEventListener('resize', syncActiveFromScroll);
            syncActiveFromScroll();
        }

        const hash = (window.location.hash || '').replace(/^#/, '');
        if (hash && sectionIds.includes(hash)) {
            setActive(hash);
        }
    });
}

/** Blog glossary image zoom (was inline <script>/<style> in blog-content-h inside #app). */
export function initBlogImageLightbox() {
    if (typeof document === 'undefined') return;

    const TARGET_ALT = 'Glossary Alt 2';

    function normAlt(value) {
        return String(value || '')
            .replace(/\u00a0/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    function cmsRoot() {
        return document.querySelector('[data-blog-content-cms]') || document.getElementById('blog-content-cms');
    }

    function wireLightbox() {
        const lb = document.getElementById('blog-image-lightbox');
        const lbImg = document.getElementById('blog-image-lightbox-img');
        const lbClose = document.getElementById('blog-image-lightbox-close');
        const lbBackdrop = document.getElementById('blog-image-lightbox-backdrop');
        if (!lb || !lbImg || lb.getAttribute('data-lightbox-wired') === '1') return;

        lb.setAttribute('data-lightbox-wired', '1');
        if (lb.parentElement !== document.body) {
            document.body.appendChild(lb);
        }

        function openLightbox(src, alt) {
            lbImg.src = src;
            lbImg.alt = alt || '';
            lb.classList.remove('hidden');
            lb.style.display = 'block';
            lb.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        }

        function closeLightbox() {
            lb.classList.add('hidden');
            lb.style.display = '';
            lb.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            lbImg.removeAttribute('src');
            lbImg.alt = '';
        }

        function onDocClick(event) {
            const target = event.target;
            if (!target || String(target.tagName).toUpperCase() !== 'IMG') return;
            if (target.closest && target.closest('#blog-image-lightbox')) return;

            const cms = cmsRoot();
            if (!cms || !cms.contains(target)) return;
            if (normAlt(target.getAttribute('alt')) !== TARGET_ALT) return;

            if (target.closest && target.closest('a')) {
                event.preventDefault();
                event.stopPropagation();
            }

            const src = target.currentSrc || target.src;
            if (!src) return;

            event.preventDefault();
            event.stopPropagation();
            openLightbox(src, TARGET_ALT);
        }

        document.addEventListener('click', onDocClick, true);
        if (lbBackdrop) lbBackdrop.addEventListener('click', closeLightbox);
        if (lbClose) lbClose.addEventListener('click', closeLightbox);
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && lb && !lb.classList.contains('hidden')) {
                closeLightbox();
            }
        });
    }

    wireLightbox();
}

/** Mutual-exclusivity for <x-faq> details groups (was inline <script> inside #app). */
export function initFaqDetailsGroups() {
    if (typeof document === 'undefined') return;
    if (window.__faqDetailsGroupsBound) return;
    if (!document.querySelector('[data-faq-group]')) return;

    window.__faqDetailsGroupsBound = true;

    document.addEventListener('toggle', function (event) {
        const target = event.target;
        if (!target || target.tagName !== 'DETAILS' || !target.open) return;

        const group = target.closest('[data-faq-group]');
        if (!group) return;

        const name = target.getAttribute('name');
        if (!name) return;

        group.querySelectorAll('details[name="' + name + '"][open]').forEach(function (other) {
            if (other !== target) other.open = false;
        });
    }, true);
}

/**
 * Country selector on /payment-methods (and mobile variant) toggles which
 * `.country-content` block is visible. Used to live as an inline <script>
 * inside #app and was stripped by Vue's template compiler.
 */
export function registerShowCountryMethods() {
    if (typeof window === 'undefined') return;
    if (typeof window.showCountryMethods === 'function') return;

    window.showCountryMethods = function showCountryMethods(value) {
        const container = document.getElementById('country-methods');
        if (!container) return;

        const blocks = document.querySelectorAll('.country-content');
        const noteById = document.getElementById('no-country-note');
        const noteByClass = document.querySelector('.country-note');

        if (!value) {
            container.classList.add('hidden');
            blocks.forEach((el) => el.classList.add('hidden'));
            if (noteById) noteById.classList.remove('hidden');
            if (noteByClass) noteByClass.classList.remove('hidden');
            return;
        }

        container.classList.remove('hidden');
        if (noteById) noteById.classList.add('hidden');
        if (noteByClass) noteByClass.classList.add('hidden');

        blocks.forEach((el) => {
            if (el.id === value + '-methods') {
                el.classList.remove('hidden');
            } else {
                el.classList.add('hidden');
            }
        });
    };
}
